/**
 * Call-interruption rules for voice-note recording (pure — unit-tested).
 *
 * The recorder OBSERVES the voice-call store only (never modifies the call
 * stack). Any call becoming locally active while recording auto-stops and
 * queues the partial audio as its own note; once the call fully ends, the UI
 * offers "Resume recording?" which starts a NEW note on the same session.
 */

import type { VoiceCallPhase } from '../../../types/voice-call'

/**
 * Phases that occupy (or are about to occupy) the LOCAL microphone / user's
 * attention. `elsewhere` is excluded — the call lives on another device and
 * recording here is legitimate.
 */
const LOCALLY_ENGAGED_PHASES: readonly VoiceCallPhase[] = [
  'incoming',
  'outgoing',
  'prejoin',
  'connecting',
  'active',
]

/** True when a call session should block STARTING a recording. */
export function isCallBlockingRecording(phase: VoiceCallPhase | null | undefined): boolean {
  return phase != null && LOCALLY_ENGAGED_PHASES.includes(phase)
}

/** True when a phase change must INTERRUPT an in-progress recording. */
export function shouldInterruptRecording(phase: VoiceCallPhase | null | undefined): boolean {
  return isCallBlockingRecording(phase)
}

/** True when the call is fully over, so "Resume recording?" may be offered. */
export function isCallOver(phase: VoiceCallPhase | null | undefined): boolean {
  return phase == null || phase === 'ended' || phase === 'idle'
}

export interface InterruptionWatcher {
  /** Stop observing. */
  dispose: () => void
}

/**
 * Observe call phases and fire `onInterrupt` ONCE when a call engages locally,
 * then `onCallOver` ONCE when that call fully ends. Store-agnostic: the caller
 * provides `subscribe`/`getPhase` (production wires the voice-call store).
 */
export function createCallInterruptionWatcher(opts: {
  subscribe: (listener: () => void) => () => void
  getPhase: () => VoiceCallPhase | null
  onInterrupt: () => void
  onCallOver: () => void
}): InterruptionWatcher {
  let stage: 'watching' | 'interrupted' | 'done' = 'watching'

  const check = () => {
    const phase = opts.getPhase()
    if (stage === 'watching' && shouldInterruptRecording(phase)) {
      stage = 'interrupted'
      opts.onInterrupt()
      return
    }
    if (stage === 'interrupted' && isCallOver(phase)) {
      stage = 'done'
      opts.onCallOver()
    }
  }

  const unsubscribe = opts.subscribe(check)
  // Catch a call that was already engaged at watch start.
  check()

  return {
    dispose: () => {
      stage = 'done'
      unsubscribe()
    },
  }
}
